/**
 * Shift Drill-Down Page (Phase 25.3)
 *
 * Drills into a single shift from the Shift Comparison page.
 * Shows:
 *  - Section-level (Job) breakdown table
 *  - Within-shift operator leaderboard with names
 *
 * URL filters (startDate/endDate/sopId/stationId/preset) are preserved on the
 * way in from ShiftComparisonPage so navigating back-and-forth doesn't reset
 * the viewer's context.
 */

import { useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Layers,
  Users,
  Loader2,
  Clock,
} from 'lucide-react';
import { useShiftComparisonDrillDown } from '../../hooks/useShiftComparison';
import { getScoreColor, getScoreBadgeColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { cn } from '../../lib/utils';

type Preset = '7d' | '30d' | '90d' | 'custom';

function computeRangeFromPreset(preset: Preset): {
  startDate: string;
  endDate: string;
} {
  const end = new Date();
  const start = new Date();
  const days =
    preset === '7d' ? 7 : preset === '90d' ? 90 : 30; // 30d default
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export function ShiftDrillDownPage() {
  const { shiftId } = useParams<{ shiftId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preset = (searchParams.get('preset') as Preset) ?? '30d';
  const sopId = searchParams.get('sopId') ?? '';
  const stationId = searchParams.get('stationId') ?? '';
  const customStart = searchParams.get('startDate') ?? '';
  const customEnd = searchParams.get('endDate') ?? '';

  const dateRange = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return {
        startDate: new Date(customStart).toISOString(),
        endDate: new Date(customEnd).toISOString(),
      };
    }
    return computeRangeFromPreset(preset === 'custom' ? '30d' : preset);
  }, [preset, customStart, customEnd]);

  const { data, isLoading } = useShiftComparisonDrillDown(shiftId, {
    sopId: sopId || undefined,
    stationId: stationId || undefined,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  useDocumentTitle(
    data ? `Shift: ${data.shift.name}` : 'Shift Comparison'
  );

  const backHref = `/analytics/shift-comparison?${searchParams.toString()}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backHref)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
            aria-label="Back to shift comparison"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-guild-green">
              Shift Comparison
            </p>
            <h1 className="text-xl font-bold text-gray-900">
              {isLoading ? 'Loading…' : data?.shift.name ?? 'Shift'}
            </h1>
            <p className="text-sm text-gray-500">
              {data?.shift.code ? `Code: ${data.shift.code} · ` : ''}
              {data ? formatRangeLabel(data.dateRange.startDate, data.dateRange.endDate) : ''}
            </p>
          </div>
        </div>

        {data?.summary && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-gray-400">Shift average</p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums',
                getScoreColor(data.summary.shiftAvgScore, 'text')
              )}
            >
              {data.summary.shiftAvgScore != null
                ? data.summary.shiftAvgScore.toFixed(1)
                : '--'}
            </p>
            <p className="text-xs text-gray-500">
              {data.summary.totalAssessments} assessments · {data.summary.totalOperators}{' '}
              operators · {data.summary.totalSections} jobs
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-gray-200 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : !data ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Shift not found.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {/* Sections breakdown */}
          <SectionsTable sections={data.sections} />

          {/* Operator leaderboard */}
          <OperatorLeaderboard operators={data.operatorLeaderboard} />
        </div>
      )}
    </div>
  );
}

function formatRangeLabel(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`;
}

// ============================================
// SECTIONS TABLE
// ============================================

function SectionsTable({
  sections,
}: {
  sections: Array<{
    sectionId: string;
    sectionTitle: string;
    sopId: string;
    sopTitle: string;
    avgScore: number | null;
    avgCompliance: number | null;
    avgEfficiency: number | null;
    avgSafety: number | null;
    assessmentCount: number;
    operatorCount: number;
  }>;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
        <Layers size={16} className="text-gray-400" />
        <h3 className="font-semibold text-gray-900">Jobs breakdown</h3>
      </div>
      <div className="overflow-x-auto">
        {sections.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-500">
            No assessments match the current filters.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Job
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Score
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  C / E / S
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Assess
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sections.map((s) => (
                <tr key={s.sectionId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/sops/${s.sopId}`}
                      className="text-sm font-medium text-gray-900 hover:text-guild-green"
                    >
                      {s.sectionTitle}
                    </Link>
                    <p className="text-xs text-gray-500">{s.sopTitle}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-bold tabular-nums',
                        getScoreBadgeColor(s.avgScore)
                      )}
                    >
                      {s.avgScore != null ? s.avgScore.toFixed(1) : '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs tabular-nums text-gray-600">
                    {fmtScore(s.avgCompliance)} / {fmtScore(s.avgEfficiency)} /{' '}
                    {fmtScore(s.avgSafety)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600 tabular-nums">
                    {s.assessmentCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================
// OPERATOR LEADERBOARD
// ============================================

function OperatorLeaderboard({
  operators,
}: {
  operators: Array<{
    rank: number;
    operatorId: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    avgScore: number | null;
    avgCompliance: number | null;
    avgEfficiency: number | null;
    avgSafety: number | null;
    assessmentCount: number;
  }>;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
        <Users size={16} className="text-gray-400" />
        <h3 className="font-semibold text-gray-900">Operator leaderboard</h3>
      </div>
      <div className="overflow-x-auto">
        {operators.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-500">
            No operators have assessments in this window.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Operator
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Score
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  C / E / S
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Assess
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {operators.map((op) => (
                <tr key={op.operatorId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                        #{op.rank}
                      </div>
                      <div>
                        <Link
                          to={`/operators/${op.operatorId}`}
                          className="text-sm font-medium text-gray-900 hover:text-guild-green"
                        >
                          {op.firstName} {op.lastName}
                        </Link>
                        <p className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock size={10} /> {op.employeeId}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-bold tabular-nums',
                        getScoreBadgeColor(op.avgScore)
                      )}
                    >
                      {op.avgScore != null ? op.avgScore.toFixed(1) : '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs tabular-nums text-gray-600">
                    {fmtScore(op.avgCompliance)} / {fmtScore(op.avgEfficiency)} /{' '}
                    {fmtScore(op.avgSafety)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600 tabular-nums">
                    {op.assessmentCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function fmtScore(v: number | null): string {
  return v != null ? v.toFixed(1) : '--';
}

export default ShiftDrillDownPage;
